/**
 * Pre-flight check on a key selection: how many rows the table would actually
 * store, given the rows sampled from the source file.
 *
 * The resolver keeps one row per key and drops rows whose key is incomplete, so
 * a key that repeats silently discards data. Counting distinct keys before
 * anything is created turns that into a warning at the moment the key is chosen,
 * rather than a surprise in the row count after a push.
 */

/** Separator matching the backend's buildCompositeKey (unit separator). */
const KEY_SEPARATOR = '\u001f';

export interface SampledRows {
  /** Header names, in file order. */
  header: string[];
  /** Data rows, aligned to `header`. */
  rows: string[][];
  /** True when the sample stopped short of the whole file. */
  truncated: boolean;
}

export interface KeyUniqueness {
  /** Rows examined (the sample, not necessarily the whole file). */
  sampledRows: number;
  /** Rows that would be stored — distinct, complete keys. */
  distinctKeys: number;
  /** Rows that would merge into a key already seen. */
  duplicateRows: number;
  /** Rows that would be dropped because a key part is blank. */
  incompleteKeyRows: number;
  /** True when the sample stopped short of the whole file. */
  truncated: boolean;
  /** Key columns that are not in the sampled header, so cannot be checked. */
  unknownColumns: string[];
}

/**
 * Counts what the chosen key would collapse. Returns null when there is nothing
 * to say — no sample, or no key chosen yet.
 */
export function analyzeKeyUniqueness(
  sample: SampledRows | null,
  keyColumnNames: string[],
): KeyUniqueness | null {
  if (!sample || sample.rows.length === 0 || keyColumnNames.length === 0) {
    return null;
  }

  const indexByLowerName = new Map(
    sample.header.map((name, index) => [name.trim().toLowerCase(), index]),
  );
  const unknownColumns: string[] = [];
  const keyIndexes: number[] = [];
  for (const name of keyColumnNames) {
    const index = indexByLowerName.get(name.trim().toLowerCase());
    if (index === undefined) unknownColumns.push(name);
    else keyIndexes.push(index);
  }

  // Every key part missing from the header means the check would be measuring
  // nothing; say so rather than reporting a bogus count of 1 distinct key.
  if (keyIndexes.length === 0) {
    return {
      sampledRows: sample.rows.length,
      distinctKeys: 0,
      duplicateRows: 0,
      incompleteKeyRows: 0,
      truncated: sample.truncated,
      unknownColumns,
    };
  }

  const seen = new Set<string>();
  let duplicateRows = 0;
  let incompleteKeyRows = 0;

  for (const row of sample.rows) {
    const parts = keyIndexes.map((index) => (row[index] ?? '').trim());
    if (parts.some((part) => part === '')) {
      incompleteKeyRows += 1;
      continue;
    }
    const key = parts.join(KEY_SEPARATOR);
    if (seen.has(key)) duplicateRows += 1;
    else seen.add(key);
  }

  return {
    sampledRows: sample.rows.length,
    distinctKeys: seen.size,
    duplicateRows,
    incompleteKeyRows,
    truncated: sample.truncated,
    unknownColumns,
  };
}

/** True when the selection would lose rows and deserves a warning. */
export function keySelectionLosesRows(analysis: KeyUniqueness | null): boolean {
  if (!analysis) return false;
  return analysis.duplicateRows > 0 || analysis.incompleteKeyRows > 0;
}
